import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { differenceInDays, isPast, format } from "date-fns";
import {
  ArrowLeft,
  Download,
  Trophy,
  Users,
  Code,
  Loader2,
  Gift,
  Copy,
  Check,
  Calendar,
  StopCircle,
  PlayCircle,
  AlertCircle,
  BarChart2,
  Mail
} from "lucide-react";
import { WinnersHistory } from "@/components/admin/WinnersHistory";
import { EntrantsList } from "@/components/admin/EntrantsList";
import { ContestStatusBadge, getContestStatus } from "@/components/admin/ContestStatusBadge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { getOrgForUser } from "@/lib/orgBilling";

interface Contest {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  is_active: boolean;
  referral_bonus_entries: number;
  ends_at: string | null;
  ended_at: string | null;
  organization_id: string;
}

export default function ContestAdmin() {
  const { slug } = useParams<{ slug: string }>();
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [sendKind, setSendKind] = useState<"winner" | "non_winner" | null>(null);
  const [isSendingEmails, setIsSendingEmails] = useState(false);
  const [isPickingWinner, setIsPickingWinner] = useState(false);
  const [isEndingContest, setIsEndingContest] = useState(false);
  const [embedCopied, setEmbedCopied] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate("/admin");
        return;
      }

      const { data: roles } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", session.user.id)
        .eq("role", "admin")
        .maybeSingle();

      if (!roles) {
        navigate("/admin");
        return;
      }

      setIsCheckingAuth(false);
    };

    checkAuth();
  }, [navigate]);

  const { data: contest, isLoading, refetch: refetchContest } = useQuery({
    queryKey: ["admin-contest", slug],
    queryFn: async () => {
      // 1. Get user's org
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return null;

      const { data: membership, error: memErr } = await supabase
        .from("organization_members")
        .select("organization_id")
        .eq("user_id", session.user.id)
        .limit(1)
        .maybeSingle();

      if (memErr || !membership) {
        throw new Error("Organization not found");
      }

      // 2. Fetch scoped contest
      const { data, error } = await supabase
        .from("contests")
        .select("*")
        .eq("slug", slug)
        .eq("organization_id", membership.organization_id)
        .maybeSingle();

      if (error) throw error;
      if (!data) throw new Error("Contest not found");
      return data as Contest;
    },
    enabled: !isCheckingAuth && !!slug,
  });

  // Stats query
  const { data: stats } = useQuery({
    queryKey: ["admin-contest-stats", contest?.id],
    queryFn: async () => {
      if (!contest) return { participants: 0, entries: 0 };

      const [participantsRes, entriesRes] = await Promise.all([
        supabase
          .from("participants")
          .select("id", { count: "exact", head: true })
          .eq("contest_id", contest.id),
        supabase
          .from("entries")
          .select("id", { count: "exact", head: true })
          .eq("contest_id", contest.id),
      ]);

      return {
        participants: participantsRes.count || 0,
        entries: entriesRes.count || 0,
      };
    },
    enabled: !!contest,
  });

  // Fetch Org for Plan Limits
  const { data: org } = useQuery({
    queryKey: ["admin-org-details"],
    queryFn: () => getOrgForUser(supabase),
    enabled: !isCheckingAuth,
  });

  // Fetch Active Contests Count
  const { data: activeContestsCount } = useQuery({
    queryKey: ["active-contests-count", contest?.organization_id],
    queryFn: async () => {
      if (!contest?.organization_id) return 0;
      const now = new Date().toISOString();
      const { count } = await supabase
        .from("contests")
        .select("id", { count: "exact", head: true })
        .eq("organization_id", contest.organization_id)
        .eq("is_active", true)
        .is("ended_at", null)
        .or(`ends_at.is.null,ends_at.gt.${now}`);
      return count || 0;
    },
    enabled: !!contest?.organization_id
  });

  const handleExport = async () => {
    if (!contest) return;

    // Check export limits for Free plan
    const planKey = org?.plan_key || 'free';
    const isManuallyEnded = !!contest.ended_at;
    const isExpired = contest.ends_at ? isPast(new Date(contest.ends_at)) : false;
    const isEnded = isManuallyEnded || isExpired;

    if (planKey === 'free' && !isEnded) {
      setShowExportModal(true);
      return;
    }

    setIsExporting(true);

    try {
      // Get current session to ensure we have a valid token
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error("Please sign in again to continue");
      }

      const { data, error } = await supabase.functions.invoke("admin-export", {
        body: { slug: contest.slug },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        // Try to extract error message from response body
        const errorMessage = error.context?.body?.error || error.message || "Failed to export data";
        throw new Error(errorMessage);
      }

      // Download CSV
      const csvContent = typeof data === 'string'
        ? data
        : (data instanceof Blob ? data : data?.csv || "");

      const blob = csvContent instanceof Blob
        ? csvContent
        : new Blob([csvContent], { type: "text/csv" });

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${contest.slug}-participants.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast({
        title: "Export Complete",
        description: `Exported ${data.count} participants`,
      });
    } catch (err) {
      toast({
        title: "Export Failed",
        description: err instanceof Error ? err.message : "An error occurred",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  const handleSendEmails = async () => {
    if (!contest || !sendKind) return;
    setIsSendingEmails(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error("Please sign in again");
      const { data, error } = await supabase.functions.invoke("admin-publish-draw", {
        body: { contestId: contest.id, kind: sendKind },
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (error || data?.error) throw new Error(data?.error || error?.message || "Failed to send emails");
      const queued = data?.queued ?? 0;
      toast({
        title: queued ? "Emails queued" : "No new emails",
        description: queued
          ? `Queued ${queued} ${sendKind === "winner" ? "winner" : "non-winner"} email${queued === 1 ? "" : "s"}.`
          : "Everyone in this group already has a send on file.",
      });
      setSendKind(null);
    } catch (error: any) {
      toast({ title: "Could not send emails", description: error.message, variant: "destructive" });
    } finally {
      setIsSendingEmails(false);
    }
  };

  const handlePickWinner = async () => {
    if (!contest) return;
    setIsPickingWinner(true);

    try {
      // Get current session to ensure we have a valid token
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error("Please sign in again to continue");
      }

      const { data, error } = await supabase.functions.invoke("admin-pick-winner", {
        body: { slug: contest.slug },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        // Try to extract error message from response body
        const errorMessage = error.context?.body?.error || error.message || "Failed to pick winner";
        throw new Error(errorMessage);
      }

      if (!data.ok) {
        throw new Error(data.error || "Failed to pick winner");
      }

      // Refresh winners list
      queryClient.invalidateQueries({ queryKey: ["winners", contest.id] });

      toast({
        title: "🎉 Winner Selected!",
        description: data.winner.email
          ? `Winner: ${data.winner.email} (${data.winner.totalEntries} entries)`
          : `Winner selected with ${data.winner.totalEntries} entries`,
      });
    } catch (err) {
      toast({
        title: "Failed to Pick Winner",
        description: err instanceof Error ? err.message : "An error occurred",
        variant: "destructive",
      });
    } finally {
      setIsPickingWinner(false);
    }
  };

  const handleEndContest = async (end: boolean) => {
    if (!contest) return;

    // Check limits if reopening
    if (!end) {
      const planKey = org?.plan_key || 'free';
      const limit = planKey === 'free' ? 1 : (planKey === 'starter' ? 3 : Infinity);

      // If we are already at/over limit, block reopening
      if (limit !== Infinity && (activeContestsCount ?? 0) >= limit) {
        setShowUpgradeModal(true);
        return;
      }
    }

    setIsEndingContest(true);

    try {
      // Get current session to ensure we have a valid token
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error("Please sign in again to continue");
      }

      const { data, error } = await supabase.functions.invoke("admin-end-contest", {
        body: { slug: contest.slug, end },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        // Try to extract error message from response body
        const errorMessage = error.context?.body?.error || error.message || "Failed to update contest";
        throw new Error(errorMessage);
      }

      if (!data.ok) {
        throw new Error(data.error || "Failed to update contest");
      }

      // Refresh contest data
      await refetchContest();

      // Invalidate related caches to refresh stats and contest state
      queryClient.invalidateQueries({ queryKey: ["admin-contest-stats", contest.id] });
      queryClient.invalidateQueries({ queryKey: ["admin-contest", slug] });
      queryClient.invalidateQueries({ queryKey: ["winners", contest.id] });

      toast({
        title: end ? "Contest Ended" : "Contest Reopened",
        description: end
          ? "No new entries will be accepted"
          : "Entries are now accepted again",
      });
    } catch (err) {
      toast({
        title: "Failed to Update Contest",
        description: err instanceof Error ? err.message : "An error occurred",
        variant: "destructive",
      });
    } finally {
      setIsEndingContest(false);
    }
  };

  const contestUrl = contest
    ? `${window.location.origin}/c/${contest.slug}`
    : "";

  const embedCode = contest
    ? `<iframe src="${contestUrl}" width="100%" height="600" frameborder="0" style="border-radius: 12px; max-width: 500px;"></iframe>`
    : "";

  const copyEmbedCode = () => {
    navigator.clipboard.writeText(embedCode);
    setEmbedCopied(true);
    setTimeout(() => setEmbedCopied(false), 2000);
  };

  const copyDirectLink = () => {
    navigator.clipboard.writeText(contestUrl);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  if (isCheckingAuth || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!contest) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <h1 className="font-heading text-2xl font-bold mb-2">Contest Not Found</h1>
          <Button asChild variant="outline">
            <Link to="/admin/dashboard">Back to Dashboard</Link>
          </Button>
        </div>
      </div>
    );
  }

  const contestStatus = getContestStatus(contest.is_active, contest.ended_at, contest.ends_at);
  const isContestEnded = contestStatus === "ended";
  const canAcceptEntries = contestStatus === "active";

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="container mx-auto px-4 py-4">
          <Button asChild variant="outline" size="sm" className="mb-4">
            <Link to="/admin/dashboard">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Link>
          </Button>
          <div className="flex items-start justify-between">
            <div>
              <h1 className="font-heading text-2xl font-bold">{contest.name}</h1>
              <p className="text-muted-foreground">/c/{contest.slug}</p>
            </div>
            {canAcceptEntries ? (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
                </span>
                Active
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-500 border border-gray-200">
                Ended
              </span>
            )}
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 space-y-8">
        {/* Stats */}
        <div className="grid gap-4 md:grid-cols-3">
          <div className="bg-card rounded-xl border border-border p-6">
            <div className="flex items-center gap-3 mb-2">
              <Users className="w-5 h-5 text-primary" />
              <span className="text-muted-foreground">Participants</span>
            </div>
            <p className="font-heading text-3xl font-bold">{stats?.participants || 0}</p>
          </div>
          <div className="bg-card rounded-xl border border-border p-6">
            <div className="flex items-center gap-3 mb-2">
              <Gift className="w-5 h-5 text-accent" />
              <span className="text-muted-foreground">Total Entries</span>
            </div>
            <p className="font-heading text-3xl font-bold">{stats?.entries || 0}</p>
          </div>
          <div className="bg-card rounded-xl border border-border p-6">
            <div className="flex items-center gap-3 mb-2">
              <Calendar className="w-5 h-5 text-primary" />
              <span className="text-muted-foreground">Days Left</span>
            </div>
            <p className="font-heading text-3xl font-bold">
              {(() => {
                if (contest.ended_at) return "Ended";
                if (!contest.ends_at) return "No end date";
                const endDate = new Date(contest.ends_at);
                if (isPast(endDate)) return "Expired";
                const days = Math.ceil(differenceInDays(endDate, new Date()));
                return days === 0 ? "Today" : days;
              })()}
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="bg-card rounded-xl border border-border p-6">
          <h2 className="font-heading text-lg font-bold mb-4">Actions</h2>
          <div className="space-y-4">
            <div className="flex flex-wrap gap-4">
            <Button asChild variant="outline">
              <Link to={`/admin/contests/${contest.slug}/edit`}>
                Edit Contest
              </Link>
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate(`/admin/analytics?contestId=${contest.id}`)}
            >
              <BarChart2 className="h-4 w-4 mr-2" />
              Analytics
            </Button>
            <Button onClick={() => handleExport()} disabled={isExporting} variant="outline">
              {isExporting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              Export CSV
            </Button>
            <Button onClick={handlePickWinner} disabled={isPickingWinner} variant="entry">
              {isPickingWinner ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Trophy className="w-4 h-4 mr-2" />
              )}
              Pick Winner
            </Button>

            {/* End/Reopen Contest Button */}
            {isContestEnded ? (
              <Button
                onClick={() => handleEndContest(false)}
                disabled={isEndingContest}
                variant="outline"
              >
                {isEndingContest ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <PlayCircle className="w-4 h-4 mr-2" />
                )}
                Reopen Contest
              </Button>
            ) : (
              <Button
                onClick={() => handleEndContest(true)}
                disabled={isEndingContest || !canAcceptEntries}
                variant="destructive"
                className="hover:bg-red-600 hover:text-white"
              >
                {isEndingContest ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <StopCircle className="w-4 h-4 mr-2" />
                )}
                End Contest
              </Button>
            )}
            </div>
            <div className="flex flex-wrap gap-4">
              <Button asChild variant="outline">
                <Link to={`/admin/contests/${contest.slug}/emails`}>
                  <Mail className="w-4 h-4 mr-2" />
                  Edit Emails
                </Link>
              </Button>
              <Button onClick={() => setSendKind("winner")} disabled={isSendingEmails} variant="outline">
                <Mail className="w-4 h-4 mr-2" />
                Send Winner Emails
              </Button>
              <Button onClick={() => setSendKind("non_winner")} disabled={isSendingEmails} variant="outline">
                <Mail className="w-4 h-4 mr-2" />
                Send Non-Winner Emails
              </Button>
            </div>
          </div>
        </div>

        {/* Winners History */}
        <div className="bg-card rounded-xl border border-border p-6">
          <div className="flex items-center gap-2 mb-4">
            <Trophy className="w-5 h-5 text-success" />
            <h2 className="font-heading text-lg font-bold">Winners History</h2>
          </div>
          <WinnersHistory contestId={contest.id} />
        </div>

        {/* Share Links */}
        <div className="bg-card rounded-xl border border-border p-6 space-y-6">
          <div className="flex items-center gap-2 mb-2">
            <Code className="w-5 h-5 text-primary" />
            <h2 className="font-heading text-lg font-bold">Share & Embed</h2>
          </div>

          {/* Direct Link */}
          <div>
            <h3 className="font-medium mb-2">Direct Link</h3>
            <p className="text-muted-foreground text-sm mb-3">
              Share this URL directly with participants:
            </p>
            <div className="flex gap-2">
              <div className="flex-1 bg-muted rounded-lg px-4 py-2 font-mono text-sm overflow-x-auto">
                {contestUrl}
              </div>
              <Button onClick={copyDirectLink} variant="outline" size="sm">
                {linkCopied ? (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4 mr-2" />
                    Copy
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Embed Code */}
          <div>
            <h3 className="font-medium mb-2">Embed via iFrame</h3>
            <p className="text-muted-foreground text-sm mb-3">
              Copy this code to embed the contest on any website:
            </p>
            <div className="bg-muted rounded-lg p-4 font-mono text-sm overflow-x-auto mb-3">
              {embedCode}
            </div>
            <Button onClick={copyEmbedCode} variant="outline" size="sm">
              {embedCopied ? (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4 mr-2" />
                  Copy Embed Code
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Preview link */}
        <div className="text-center">
          <Button asChild variant="ghost">
            <a href={`/c/${contest.slug}`} target="_blank" rel="noopener noreferrer">
              Preview Contest →
            </a>
          </Button>
        </div>

        {/* Participants & Abuse Prevention */}
        <div className="bg-card rounded-xl border border-border p-6">
          <EntrantsList
            contestId={contest.id}
            organizationId={contest.organization_id}
            planKey={org?.plan_key || 'free'}
          />
        </div>
        {/* Upgrade Modal */}
        <Dialog open={showUpgradeModal} onOpenChange={setShowUpgradeModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>You’re at your active contest limit</DialogTitle>
              <DialogDescription className="space-y-3 pt-2">
                <p>Your current <strong>{org?.plan_key || 'free'}</strong> plan allows for <strong>{org?.plan_key === 'starter' ? '3' : '1'}</strong> active contest{org?.plan_key === 'free' ? '' : 's'}.</p>
                <ul className="list-disc pl-5 space-y-1 text-sm">
                  <li><strong>Free Plan:</strong> 1 active contest</li>
                  <li><strong>Starter Plan:</strong> 3 active contests</li>
                  <li><strong>Growth Plan:</strong> Unlimited contests</li>
                </ul>
                <p>Upgrade your plan to reopen this contest.</p>
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button variant="outline" onClick={() => setShowUpgradeModal(false)}>Close</Button>
              <Button asChild onClick={() => setShowUpgradeModal(false)}>
                <Link to="/admin/settings?tab=billing">
                  Upgrade to {org?.plan_key === 'free' ? 'Starter' : 'Growth'}
                </Link>
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Export Limit Modal */}
        <Dialog open={showExportModal} onOpenChange={setShowExportModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Export available after contest ends</DialogTitle>
              <DialogDescription className="space-y-3 pt-2">
                <p>On the Free plan, the full entrant list can only be exported after the contest ends.</p>
                <p>End the contest to export now, or upgrade for anytime export.</p>
                {contest?.ends_at && !contest.ended_at && !isPast(new Date(contest.ends_at)) && (
                  <p className="text-sm text-muted-foreground mt-2">
                    This contest ends on {format(new Date(contest.ends_at), "PPP p")}.
                  </p>
                )}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button variant="outline" onClick={() => setShowExportModal(false)}>Close</Button>
              <Button asChild onClick={() => setShowExportModal(false)}>
                <Link to="/admin/settings?tab=billing">
                  Upgrade to Starter
                </Link>
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={!!sendKind} onOpenChange={(open) => { if (!open) setSendKind(null); }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{sendKind === "winner" ? "Send Winner Emails?" : "Send Non-Winner Emails?"}</DialogTitle>
              <DialogDescription>
                {sendKind === "winner"
                  ? "This emails each picked winner. People who already received the winner email are skipped."
                  : "This emails everyone who entered, opted in, and did not win. People who already received the non-winner email are skipped."}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setSendKind(null)}>Cancel</Button>
              <Button onClick={handleSendEmails} disabled={isSendingEmails}>
                {isSendingEmails && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Send emails
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
